import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  Package, Edit, Save, Plus, Trash2, Loader2, Star, Check, X,
  CreditCard, Calendar, Users, BarChart3, Mail, Globe, Image, Code
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";

const LANGUAGES = [
  { code: "it", name: "Italiano", flag: "🇮🇹" },
  { code: "en", name: "English", flag: "🇬🇧" },
  { code: "de", name: "Deutsch", flag: "🇩🇪" },
  { code: "es", name: "Español", flag: "🇪🇸" }
];

const PlansManager = () => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [saving, setSaving] = useState(false);
  const [selectedLang, setSelectedLang] = useState("it");
  const [featuresText, setFeaturesText] = useState("");

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API}/superadmin/plans`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPlans(res.data);
    } catch (error) {
      toast.error("Errore nel caricamento piani");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (plan) => {
    setEditingPlan({ ...plan });
    setSelectedLang("it");
    setFeaturesText((plan.features || []).join("\n"));
    setEditDialogOpen(true);
  };

  const handleCreate = () => {
    setEditingPlan({
      plan_id: "",
      name: "",
      name_en: "",
      name_de: "",
      name_es: "",
      description: "",
      description_en: "",
      description_de: "",
      description_es: "",
      price_monthly: 0,
      price_yearly: 0,
      currency: "EUR",
      features: [],
      features_en: [],
      features_de: [],
      features_es: [],
      max_calendars: 1,
      max_bookings_per_month: 50,
      max_staff: 1,
      max_services: 5,
      max_customers: 50,
      stripe_enabled: false,
      google_calendar_enabled: false,
      email_templates_enabled: false,
      custom_branding_enabled: false,
      iframe_widget_enabled: false,
      cloudinary_images_enabled: false,
      crm_full_enabled: false,
      analytics_charts_enabled: false,
      analytics_export_enabled: false,
      multilanguage_enabled: false,
      support_level: "docs",
      is_active: true,
      is_popular: false,
      order: plans.length
    });
    setSelectedLang("it");
    setFeaturesText("");
    setEditDialogOpen(true);
  };

  const handleSave = async () => {
    if (!editingPlan.name) {
      toast.error("Il nome è obbligatorio");
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem("token");
      
      // Parse features from text
      const currentFeatures = featuresText.split("\n").filter(f => f.trim());
      const planData = {
        ...editingPlan,
        [`features${selectedLang === "it" ? "" : "_" + selectedLang}`]: currentFeatures
      };
      
      if (editingPlan.plan_id) {
        // Update existing
        await axios.put(`${API}/superadmin/plans/${editingPlan.plan_id}`, planData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Piano aggiornato");
      } else {
        // Create new
        planData.plan_id = `plan_${Date.now()}`;
        await axios.post(`${API}/superadmin/plans`, planData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Piano creato");
      }
      
      setEditDialogOpen(false);
      fetchPlans();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (planId) => {
    if (!confirm("Sei sicuro di voler eliminare questo piano?")) return;
    
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API}/superadmin/plans/${planId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Piano eliminato");
      fetchPlans();
    } catch (error) {
      toast.error("Errore nell'eliminazione");
    }
  };

  const handleResetDefaults = async () => {
    if (!confirm("Questo ripristinerà tutti i piani ai valori di default. Continuare?")) return;
    
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API}/superadmin/plans/reset-defaults`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Piani ripristinati");
      fetchPlans();
    } catch (error) {
      toast.error("Errore nel ripristino");
    }
  };

  const handleLangChange = (newLang) => {
    // Save current features before switching
    if (editingPlan) {
      const currentFeatures = featuresText.split("\n").filter(f => f.trim());
      const featureKey = selectedLang === "it" ? "features" : `features_${selectedLang}`;
      setEditingPlan({ ...editingPlan, [featureKey]: currentFeatures });
    }
    
    // Load features for new language
    setSelectedLang(newLang);
    const newFeatureKey = newLang === "it" ? "features" : `features_${newLang}`;
    setFeaturesText((editingPlan?.[newFeatureKey] || []).join("\n"));
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(price);
  };

  const formatLimit = (value) => {
    return value === -1 ? "∞" : value;
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Package className="h-5 w-5 text-purple-600" />
            </div>
            <div>
              <CardTitle>Gestione Piani</CardTitle>
              <CardDescription>Configura i piani di abbonamento e le funzionalità</CardDescription>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleResetDefaults} data-testid="reset-plans-btn">
              Ripristina Default
            </Button>
            <Button onClick={handleCreate} data-testid="add-plan-btn">
              <Plus className="h-4 w-4 mr-2" />Nuovo Piano
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid md:grid-cols-3 gap-6">
          {plans.map((plan) => (
            <Card 
              key={plan.plan_id} 
              className={`relative ${plan.is_popular ? 'border-2 border-primary ring-2 ring-primary/20' : ''}`}
              data-testid={`plan-card-${plan.plan_id}`}
            >
              {plan.is_popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <Badge className="bg-primary text-white">
                    <Star className="h-3 w-3 mr-1" />Più Popolare
                  </Badge>
                </div>
              )}
              <CardContent className="p-6">
                <div className="text-center mb-4">
                  <h3 className="text-xl font-bold">{plan.name}</h3>
                  <p className="text-sm text-slate-500">{plan.description}</p>
                  <div className="mt-4">
                    <span className="text-4xl font-bold">{formatPrice(plan.price_monthly)}</span>
                    <span className="text-slate-500">/mese</span>
                  </div>
                  {plan.price_yearly > 0 && (
                    <p className="text-sm text-green-600">
                      {formatPrice(plan.price_yearly / 12)}/mese se annuale
                    </p>
                  )}
                </div>

                {/* Limits */}
                <div className="grid grid-cols-2 gap-2 mb-4 text-sm">
                  <div className="flex items-center gap-1">
                    <Calendar className="h-3 w-3 text-slate-400" />
                    <span>{formatLimit(plan.max_calendars)} calendari</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Users className="h-3 w-3 text-slate-400" />
                    <span>{formatLimit(plan.max_staff)} staff</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Package className="h-3 w-3 text-slate-400" />
                    <span>{formatLimit(plan.max_services)} servizi</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <BarChart3 className="h-3 w-3 text-slate-400" />
                    <span>{formatLimit(plan.max_bookings_per_month)}/mese</span>
                  </div>
                </div>

                {/* Features */}
                <div className="space-y-2 mb-4">
                  {(plan.features || []).slice(0, 6).map((feature, idx) => (
                    <div key={idx} className="flex items-start gap-2 text-sm">
                      <Check className="h-4 w-4 text-green-500 shrink-0 mt-0.5" />
                      <span>{feature}</span>
                    </div>
                  ))}
                  {(plan.features || []).length > 6 && (
                    <p className="text-xs text-slate-500 pl-6">
                      +{plan.features.length - 6} altre funzionalità
                    </p>
                  )}
                </div>

                {/* Feature Flags Icons */}
                <div className="flex flex-wrap gap-2 mb-4">
                  {plan.stripe_enabled && <Badge variant="outline" className="text-xs"><CreditCard className="h-3 w-3 mr-1"/>Stripe</Badge>}
                  {plan.google_calendar_enabled && <Badge variant="outline" className="text-xs"><Calendar className="h-3 w-3 mr-1"/>GCal</Badge>}
                  {plan.email_templates_enabled && <Badge variant="outline" className="text-xs"><Mail className="h-3 w-3 mr-1"/>Email</Badge>}
                  {plan.multilanguage_enabled && <Badge variant="outline" className="text-xs"><Globe className="h-3 w-3 mr-1"/>i18n</Badge>}
                  {plan.iframe_widget_enabled && <Badge variant="outline" className="text-xs"><Code className="h-3 w-3 mr-1"/>Widget</Badge>}
                  {plan.cloudinary_images_enabled && <Badge variant="outline" className="text-xs"><Image className="h-3 w-3 mr-1"/>Img</Badge>}
                </div>

                <div className="flex gap-2">
                  <Button variant="outline" className="flex-1" onClick={() => handleEdit(plan)}>
                    <Edit className="h-4 w-4 mr-2" />Modifica
                  </Button>
                  {plan.plan_id !== "plan_starter" && (
                    <Button variant="ghost" size="icon" className="text-red-500" onClick={() => handleDelete(plan.plan_id)}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </CardContent>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingPlan?.plan_id ? `Modifica Piano: ${editingPlan.name}` : "Nuovo Piano"}
            </DialogTitle>
          </DialogHeader>
          
          {editingPlan && (
            <Tabs defaultValue="general" className="w-full">
              <TabsList className="grid w-full grid-cols-4">
                <TabsTrigger value="general">Generale</TabsTrigger>
                <TabsTrigger value="limits">Limiti</TabsTrigger>
                <TabsTrigger value="features">Funzionalità</TabsTrigger>
                <TabsTrigger value="translations">Traduzioni</TabsTrigger>
              </TabsList>

              {/* General Tab */}
              <TabsContent value="general" className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Nome Piano *</Label>
                    <Input
                      value={editingPlan.name}
                      onChange={(e) => setEditingPlan({ ...editingPlan, name: e.target.value })}
                      placeholder="Es: Pro"
                      data-testid="plan-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Ordine Visualizzazione</Label>
                    <Input
                      type="number"
                      value={editingPlan.order}
                      onChange={(e) => setEditingPlan({ ...editingPlan, order: parseInt(e.target.value) || 0 })}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Descrizione (IT)</Label>
                  <Textarea
                    value={editingPlan.description || ""}
                    onChange={(e) => setEditingPlan({ ...editingPlan, description: e.target.value })}
                    placeholder="Descrizione breve del piano..."
                  />
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Prezzo Mensile (€)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={editingPlan.price_monthly}
                      onChange={(e) => setEditingPlan({ ...editingPlan, price_monthly: parseFloat(e.target.value) || 0 })}
                      data-testid="plan-price"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Prezzo Annuale (€)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={editingPlan.price_yearly || 0}
                      onChange={(e) => setEditingPlan({ ...editingPlan, price_yearly: parseFloat(e.target.value) || 0 })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Livello Supporto</Label>
                    <Select
                      value={editingPlan.support_level}
                      onValueChange={(v) => setEditingPlan({ ...editingPlan, support_level: v })}
                    >
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="docs">Solo Documentazione</SelectItem>
                        <SelectItem value="email">Email (24h)</SelectItem>
                        <SelectItem value="priority">Prioritario</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="flex items-center gap-6 p-4 bg-slate-50 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={editingPlan.is_active}
                      onCheckedChange={(v) => setEditingPlan({ ...editingPlan, is_active: v })}
                    />
                    <Label>Piano Attivo</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={editingPlan.is_popular}
                      onCheckedChange={(v) => setEditingPlan({ ...editingPlan, is_popular: v })}
                    />
                    <Label>Mostra come "Più Popolare"</Label>
                  </div>
                </div>
              </TabsContent>

              {/* Limits Tab */}
              <TabsContent value="limits" className="space-y-4 mt-4">
                <p className="text-sm text-slate-500">Usa -1 per illimitato</p>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Max Calendari</Label>
                    <Input
                      type="number"
                      value={editingPlan.max_calendars}
                      onChange={(e) => setEditingPlan({ ...editingPlan, max_calendars: parseInt(e.target.value) })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Max Prenotazioni/Mese</Label>
                    <Input
                      type="number"
                      value={editingPlan.max_bookings_per_month}
                      onChange={(e) => setEditingPlan({ ...editingPlan, max_bookings_per_month: parseInt(e.target.value) })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Max Staff</Label>
                    <Input
                      type="number"
                      value={editingPlan.max_staff}
                      onChange={(e) => setEditingPlan({ ...editingPlan, max_staff: parseInt(e.target.value) })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Max Servizi</Label>
                    <Input
                      type="number"
                      value={editingPlan.max_services}
                      onChange={(e) => setEditingPlan({ ...editingPlan, max_services: parseInt(e.target.value) })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Max Clienti</Label>
                    <Input
                      type="number"
                      value={editingPlan.max_customers}
                      onChange={(e) => setEditingPlan({ ...editingPlan, max_customers: parseInt(e.target.value) })}
                    />
                  </div>
                </div>
              </TabsContent>

              {/* Features Tab */}
              <TabsContent value="features" className="space-y-4 mt-4">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {[
                    { key: "stripe_enabled", label: "Pagamenti Stripe", icon: CreditCard },
                    { key: "google_calendar_enabled", label: "Google Calendar", icon: Calendar },
                    { key: "email_templates_enabled", label: "Email Personalizzate", icon: Mail },
                    { key: "custom_branding_enabled", label: "Branding Personalizzato", icon: Star },
                    { key: "iframe_widget_enabled", label: "Widget Iframe", icon: Code },
                    { key: "cloudinary_images_enabled", label: "Immagini Servizi", icon: Image },
                    { key: "crm_full_enabled", label: "CRM Completo", icon: Users },
                    { key: "analytics_charts_enabled", label: "Analytics Grafici", icon: BarChart3 },
                    { key: "analytics_export_enabled", label: "Export Report", icon: Package },
                    { key: "multilanguage_enabled", label: "Multi-lingua", icon: Globe },
                  ].map(({ key, label, icon: Icon }) => (
                    <div key={key} className="flex items-center gap-3 p-3 border rounded-lg">
                      <Switch
                        checked={editingPlan[key]}
                        onCheckedChange={(v) => setEditingPlan({ ...editingPlan, [key]: v })}
                      />
                      <Icon className="h-4 w-4 text-slate-500" />
                      <Label className="cursor-pointer">{label}</Label>
                    </div>
                  ))}
                </div>
              </TabsContent>

              {/* Translations Tab */}
              <TabsContent value="translations" className="space-y-4 mt-4">
                <div className="flex gap-2 mb-4">
                  {LANGUAGES.map((lang) => (
                    <Button
                      key={lang.code}
                      variant={selectedLang === lang.code ? "default" : "outline"}
                      size="sm"
                      onClick={() => handleLangChange(lang.code)}
                    >
                      {lang.flag} {lang.name}
                    </Button>
                  ))}
                </div>

                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>Nome ({LANGUAGES.find(l => l.code === selectedLang)?.name})</Label>
                    <Input
                      value={editingPlan[selectedLang === "it" ? "name" : `name_${selectedLang}`] || ""}
                      onChange={(e) => setEditingPlan({
                        ...editingPlan,
                        [selectedLang === "it" ? "name" : `name_${selectedLang}`]: e.target.value
                      })}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Descrizione ({LANGUAGES.find(l => l.code === selectedLang)?.name})</Label>
                    <Textarea
                      value={editingPlan[selectedLang === "it" ? "description" : `description_${selectedLang}`] || ""}
                      onChange={(e) => setEditingPlan({
                        ...editingPlan,
                        [selectedLang === "it" ? "description" : `description_${selectedLang}`]: e.target.value
                      })}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Funzionalità ({LANGUAGES.find(l => l.code === selectedLang)?.name})</Label>
                    <p className="text-xs text-slate-500">Una funzionalità per riga</p>
                    <Textarea
                      value={featuresText}
                      onChange={(e) => setFeaturesText(e.target.value)}
                      placeholder="Es:\n300 prenotazioni/mese\n3 calendari\nServizi illimitati"
                      className="min-h-[200px] font-mono text-sm"
                    />
                  </div>
                </div>
              </TabsContent>
            </Tabs>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>Annulla</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="save-plan-btn">
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
              Salva Piano
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default PlansManager;
